
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function CustomTraining() {
  const problems = [
    {
      icon: 'ri-user-line',
      title: 'One-Size-Fits-All Approach',
      description: 'Generic training programs ignore your unique business model, industry challenges, and customer base.'
    },
    {
      icon: 'ri-time-line',
      title: 'Wasted Time and Resources',
      description: 'Teams spend hours learning irrelevant strategies that don\'t apply to their actual sales scenarios.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Poor ROI',
      description: 'Without customization, training fails to deliver measurable improvements in performance and revenue.'
    },
    {
      icon: 'ri-team-line',
      title: 'Low Engagement',
      description: 'Sales reps disengage when training content doesn\'t reflect their real-world challenges and opportunities.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-target-line',
      title: 'Your Framework, Your Training',
      description: 'Every module is built around your specific sales framework, ensuring perfect alignment with your strategy.'
    },
    {
      icon: 'ri-user-voice-line',
      title: 'Real Scenarios, Real Practice',
      description: 'Role play sessions use your actual customer personas, objections, and sales situations.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Measurable Results',
      description: 'Track progress with metrics that matter to your business, not generic benchmarks.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Faster Implementation',
      description: 'Teams can immediately apply what they learn because it\'s designed for their specific context.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-teal-900 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://public.readdy.ai/ai/img_res/3c73ca063a4e6cf93a80ddad9e2cf65d.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Sales Training <span className="text-teal-400">Designed for Your Business</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
              Stop wasting time on generic training programs. Get customized sales training built around your framework, your customers, and your goals.
            </p>
            <Link
              to="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors inline-block whitespace-nowrap cursor-pointer"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Why Generic Training Fails */}
      <section className="py-20 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Generic Training Fails
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Most sales training programs are built for everyone, which means they're perfect for no one. Here's why that approach doesn't work.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {problems.map((problem, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${problem.icon} text-2xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {problem.title}
                </h3>
                <p className="text-gray-600">
                  {problem.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Custom Training Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How Training Based on Your Framework Works
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Framework Analysis</h3>
                    <p className="text-gray-600">We analyze your AI-generated sales framework to understand your unique process, challenges, and opportunities.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Custom Module Creation</h3>
                    <p className="text-gray-600">Every training module is built specifically for your business, incorporating your products, customers, and sales process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Role Play Integration</h3>
                    <p className="text-gray-600">AI-powered role play sessions use your actual customer scenarios, objections, and sales situations for realistic practice.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Continuous Refinement</h3>
                    <p className="text-gray-600">Training evolves with your business, incorporating new products, market changes, and performance insights.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Custom%20sales%20training%20dashboard%20showing%20personalized%20modules%2C%20progress%20tracking%2C%20and%20business-specific%20content.%20Modern%20interface%20with%20charts%2C%20training%20paths%2C%20and%20customization%20options.%20Professional%20design%20with%20teal%20accents%20and%20clean%20layout&width=600&height=400&seq=custom-training&orientation=landscape"
                alt="Custom Training Platform"
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Role Play Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Role Play: The Non-Negotiable Element
            </h2>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Training without practice is just information. Role play transforms knowledge into skills, and skills into results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">Safe Practice Environment</h3>
              <p className="text-teal-100">
                Make mistakes and learn without the pressure of real customer interactions.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-feedback-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">Instant Feedback</h3>
              <p className="text-teal-100">
                AI provides immediate, specific feedback on performance, technique, and areas for improvement.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-repeat-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">Unlimited Practice</h3>
              <p className="text-teal-100">
                Practice the same scenario multiple times until you master it, with variations to keep it challenging.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/role-play"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors inline-block whitespace-nowrap cursor-pointer"
            >
              Learn More About Role Play
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Custom Training Advantage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When training is built specifically for your business, the results speak for themselves.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready for Training That Actually Works?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Stop settling for generic solutions. Get sales training designed specifically for your business and watch your team's performance soar.
          </p>
          <Link
            to="/contact"
            className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors inline-block whitespace-nowrap cursor-pointer"
          >
            Book a Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
